import React from 'react';
import { useLocation, useNavigate, useSearchParams, matchPath } from 'react-router-dom';
import { getLocationReturnPath, useNavigateBack } from '../../../hooks/useNavigateBack';
import useSWR from 'swr';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import { formatDate as formatDateGlobal } from '../../../config/appConfig';
import { fetcher } from '../../../services/fetcher';
import { createSpareForwarding, updateSpareForwarding } from '../../../services/mutations';
import {
  Accordion,
  AccordionItem,
  Button,
  Card,
  ChevronLeftIcon,
  DateInput,
  Label,
  NumberInput,
  Select,
  Textarea,
  TextInput,
} from '../../../components/ui';
import type { SelectOption } from '../../../components/ui/Select';
import { moduleConfig } from './spareForwardingsModuleConfig';
import { moduleConfig as supplyCasesModuleConfig } from '../spare-cases/config';
import { formatCaseParenthetical } from '../requisitions/caseDisplay';
import { useSpareForwarding } from './useData';

const Form: React.FC = () => {
  const { colors } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const editMatch = matchPath({ path: '/spares/spare-forwardings/:id/edit', end: true }, location.pathname);
  const isEdit = editMatch != null;
  const rawId = editMatch?.params.id;
  const forwardingId = rawId != null && /^\d+$/.test(String(rawId)) ? Number(rawId) : undefined;

  const paramCaseId = React.useMemo(() => {
    const e = searchParams.get('caseId');
    const n = e != null ? Number(e) : NaN;
    return Number.isFinite(n) && n > 0 ? Math.floor(n) : undefined;
  }, [searchParams]);
  const paramPoId = React.useMemo(() => {
    const e = searchParams.get('purchaseOrderId');
    const n = e != null ? Number(e) : NaN;
    return Number.isFinite(n) && n > 0 ? Math.floor(n) : undefined;
  }, [searchParams]);

  const { data: existing, isLoading: loadingExisting } = useSpareForwarding(forwardingId);
  const { data: casesRes } = useSWR<any>('/spare-cases?page=1&limit=500', fetcher);
  const { data: posRes } = useSWR<any>('/spare-purchase-orders?page=1&limit=500', fetcher);
  const { data: suppliersRes } = useSWR<any>('/suppliers?page=1&limit=500', fetcher);
  const { data: portsRes } = useSWR<any>('/ports?page=1&limit=500', fetcher);
  const { data: statusesRes } = useSWR<any>('/spare-forwarding-statuses?page=1&limit=200', fetcher);
  const { data: prioritiesRes } = useSWR<any>('/spare-forwarding-priorities?page=1&limit=200', fetcher);

  const cases = React.useMemo(
    () => (Array.isArray(casesRes) ? casesRes : casesRes?.data ?? []),
    [casesRes],
  );
  const purchaseOrders = React.useMemo(
    () => (Array.isArray(posRes) ? posRes : posRes?.data ?? []),
    [posRes],
  );
  const suppliers = React.useMemo(
    () => (Array.isArray(suppliersRes) ? suppliersRes : suppliersRes?.data ?? []),
    [suppliersRes],
  );
  const ports = React.useMemo(() => (Array.isArray(portsRes) ? portsRes : portsRes?.data ?? []), [portsRes]);
  const statuses = React.useMemo(
    () => (Array.isArray(statusesRes) ? statusesRes : statusesRes?.data ?? []),
    [statusesRes],
  );
  const priorities = React.useMemo(
    () => (Array.isArray(prioritiesRes) ? prioritiesRes : prioritiesRes?.data ?? []),
    [prioritiesRes],
  );

  const [selectedCaseId, setSelectedCaseId] = React.useState('');
  const [selectedPoId, setSelectedPoId] = React.useState('');
  const [selectedForwarderId, setSelectedForwarderId] = React.useState('');
  const [selectedPortId, setSelectedPortId] = React.useState('');
  const [selectedStatusId, setSelectedStatusId] = React.useState('');
  const [selectedPriorityId, setSelectedPriorityId] = React.useState('');
  const [selectedWarehouseLocationId, setSelectedWarehouseLocationId] = React.useState('');
  const [isSaving, setIsSaving] = React.useState(false);
  const [formData, setFormData] = React.useState({
    ref: '',
    etaToPort: '',
    costUsd: '',
    packageCount: '',
    totalWeight: '',
    notes: '',
    warehouseArrivalDate: '',
    warehouseDepartureDate: '',
    warehouseInvoiceNo: '',
    warehouseCostUsd: '',
    subAgentArrivalDate: '',
    subAgentDeliveryDate: '',
    subAgentInvoiceNo: '',
    subAgentCostUsd: '',
    vesselAgentArrivalDate: '',
    vesselAgentDeliveryDate: '',
    vesselAgentInvoiceNo: '',
    vesselAgentCostUsd: '',
  });

  const warehouseLocationsKey =
    selectedForwarderId && Number(selectedForwarderId) > 0
      ? `/forwarder-warehouse-locations?page=1&limit=500&forwarderId=${selectedForwarderId}`
      : null;
  const { data: whLocRes } = useSWR<any>(warehouseLocationsKey, fetcher);
  const warehouseLocations = React.useMemo(
    () => (Array.isArray(whLocRes) ? whLocRes : whLocRes?.data ?? []),
    [whLocRes],
  );

  const posForCase = React.useMemo(() => {
    if (!selectedCaseId) return [];
    return purchaseOrders.filter((po: { caseId?: number }) => String(po.caseId) === selectedCaseId);
  }, [purchaseOrders, selectedCaseId]);

  const fmtCase = React.useCallback((iso?: string | null) => (iso ? formatDateGlobal(iso) : ''), []);

  const caseOptions = React.useMemo((): SelectOption[] => {
    const rows = cases.map((c: { id: number; ref?: string | null; caseDate?: string | null; eta?: string | null }) => ({
      value: String(c.id),
      label: formatCaseParenthetical(c, fmtCase) ?? c.ref?.trim() ?? `Case #${c.id}`,
    }));
    if (existing?.caseId && existing?.spareCase && !rows.some((o: SelectOption) => o.value === String(existing.caseId))) {
      const sc = existing.spareCase;
      rows.unshift({
        value: String(existing.caseId),
        label: formatCaseParenthetical(sc, fmtCase) ?? sc.ref?.trim() ?? `Case #${existing.caseId}`,
      });
    }
    return [{ value: '', label: 'Select case…' }, ...rows];
  }, [cases, existing, fmtCase]);

  const poOptions = React.useMemo((): SelectOption[] => {
    const rows = posForCase.map((po: { id: number; ref?: string | null }) => ({
      value: String(po.id),
      label: po.ref?.trim() ? po.ref : `PO #${po.id}`,
    }));
    if (
      existing?.purchaseOrderId &&
      existing?.purchaseOrder &&
      !rows.some((o: SelectOption) => o.value === String(existing.purchaseOrderId))
    ) {
      rows.push({
        value: String(existing.purchaseOrderId),
        label: existing.purchaseOrder.ref?.trim() || `PO #${existing.purchaseOrderId}`,
      });
    }
    return [{ value: '', label: 'None' }, ...rows];
  }, [posForCase, existing]);

  const supplierOptions = React.useMemo((): SelectOption[] => {
    const rows = suppliers.map((s: { id: number; name: string; code?: string | null }) => ({
      value: String(s.id),
      label: s.name,
    }));
    if (existing?.forwarderId && existing?.forwarder && !rows.some((o: SelectOption) => o.value === String(existing.forwarderId))) {
      const f = existing.forwarder;
      rows.push({
        value: String(existing.forwarderId),
        label: f.name,
      });
    }
    return [{ value: '', label: 'None' }, ...rows];
  }, [suppliers, existing]);

  const portOptions = React.useMemo((): SelectOption[] => {
    const rows = ports.map((p: { id: number; name: string; code?: string | null }) => ({
      value: String(p.id),
      label: p.name,
    }));
    if (existing?.portId && existing?.port && !rows.some((o: SelectOption) => o.value === String(existing.portId))) {
      const p = existing.port;
      rows.push({
        value: String(existing.portId),
        label: p.name,
      });
    }
    return [{ value: '', label: 'None' }, ...rows];
  }, [ports, existing]);

  const statusOptions = React.useMemo((): SelectOption[] => {
    const rows = statuses.map((s: { id: number; code: string; name: string }) => ({
      value: String(s.id),
      label: s.name,
    }));
    if (existing?.statusId && existing?.status && !rows.some((o: SelectOption) => o.value === String(existing.statusId))) {
      const s = existing.status;
      rows.push({ value: String(existing.statusId), label: s.name });
    }
    return [{ value: '', label: 'Default (In transit)' }, ...rows];
  }, [statuses, existing]);

  const priorityOptions = React.useMemo((): SelectOption[] => {
    const rows = priorities.map((p: { id: number; code: string; name: string }) => ({
      value: String(p.id),
      label: p.name,
    }));
    if (
      existing?.priorityId &&
      existing?.priority &&
      !rows.some((o: SelectOption) => o.value === String(existing.priorityId))
    ) {
      const pr = existing.priority;
      rows.push({ value: String(existing.priorityId), label: pr.name });
    }
    return [{ value: '', label: 'None' }, ...rows];
  }, [priorities, existing]);

  const warehouseLocationOptions = React.useMemo((): SelectOption[] => {
    const rows = warehouseLocations.map((loc: { id: number; name: string; code?: string | null }) => ({
      value: String(loc.id),
      label: loc.name,
    }));
    if (
      existing?.warehouseLocationId &&
      existing?.warehouseLocation &&
      !rows.some((o: SelectOption) => o.value === String(existing.warehouseLocationId))
    ) {
      const w = existing.warehouseLocation;
      rows.push({
        value: String(existing.warehouseLocationId),
        label: w.name,
      });
    }
    return [{ value: '', label: 'None' }, ...rows];
  }, [warehouseLocations, existing]);

  const fmtUsd = (v: string | number | null | undefined) =>
    v != null && v !== '' ? (typeof v === 'number' ? String(v) : String(v)) : '';

  React.useEffect(() => {
    if (!isEdit || !existing) return;
    setSelectedCaseId(String(existing.caseId));
    setSelectedPoId(existing.purchaseOrderId != null ? String(existing.purchaseOrderId) : '');
    setSelectedForwarderId(existing.forwarderId != null ? String(existing.forwarderId) : '');
    setSelectedPortId(existing.portId != null ? String(existing.portId) : '');
    setSelectedStatusId(existing.statusId != null ? String(existing.statusId) : '');
    setSelectedPriorityId(existing.priorityId != null ? String(existing.priorityId) : '');
    setSelectedWarehouseLocationId(existing.warehouseLocationId != null ? String(existing.warehouseLocationId) : '');
    setFormData({
      ref: existing.ref ?? '',
      etaToPort: existing.etaToPort ? String(existing.etaToPort).slice(0, 10) : '',
      costUsd: fmtUsd(existing.costUsd),
      packageCount: existing.packageCount != null ? String(existing.packageCount) : '',
      totalWeight: existing.totalWeight != null && existing.totalWeight !== '' ? String(existing.totalWeight) : '',
      notes: existing.notes ?? '',
      warehouseArrivalDate: existing.warehouseArrivalDate ? String(existing.warehouseArrivalDate).slice(0, 10) : '',
      warehouseDepartureDate: existing.warehouseDepartureDate ? String(existing.warehouseDepartureDate).slice(0, 10) : '',
      warehouseInvoiceNo: existing.warehouseInvoiceNo ?? '',
      warehouseCostUsd: fmtUsd(existing.warehouseCostUsd),
      subAgentArrivalDate: existing.subAgentArrivalDate ? String(existing.subAgentArrivalDate).slice(0, 10) : '',
      subAgentDeliveryDate: existing.subAgentDeliveryDate ? String(existing.subAgentDeliveryDate).slice(0, 10) : '',
      subAgentInvoiceNo: existing.subAgentInvoiceNo ?? '',
      subAgentCostUsd: fmtUsd(existing.subAgentCostUsd),
      vesselAgentArrivalDate: existing.vesselAgentArrivalDate ? String(existing.vesselAgentArrivalDate).slice(0, 10) : '',
      vesselAgentDeliveryDate: existing.vesselAgentDeliveryDate ? String(existing.vesselAgentDeliveryDate).slice(0, 10) : '',
      vesselAgentInvoiceNo: existing.vesselAgentInvoiceNo ?? '',
      vesselAgentCostUsd: fmtUsd(existing.vesselAgentCostUsd),
    });
  }, [isEdit, existing]);

  React.useEffect(() => {
    if (isEdit) return;
    if (paramCaseId) setSelectedCaseId(String(paramCaseId));
    if (paramPoId) setSelectedPoId(String(paramPoId));
  }, [isEdit, paramCaseId, paramPoId]);

  /** Prefer router `state.from` (e.g. case view), else new-form `caseId` query → case view, else list / history. */
  const returnPath = React.useMemo(() => {
    const fromState = getLocationReturnPath(location.state);
    if (fromState) return fromState;
    if (!isEdit && paramCaseId != null && paramCaseId > 0) {
      return supplyCasesModuleConfig.module.route.caseView(paramCaseId);
    }
    return null;
  }, [location.state, isEdit, paramCaseId]);

  const goBack = useNavigateBack(moduleConfig.module.route.list, returnPath);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedCaseId) {
      return;
    }
    setIsSaving(true);
    try {
      const payload: Record<string, unknown> = {
        caseId: Number(selectedCaseId),
        purchaseOrderId: selectedPoId ? Number(selectedPoId) : null,
        forwarderId: selectedForwarderId ? Number(selectedForwarderId) : null,
        portId: selectedPortId ? Number(selectedPortId) : null,
        etaToPort: formData.etaToPort.trim() ? formData.etaToPort.trim() : null,
        statusId: selectedStatusId ? Number(selectedStatusId) : null,
        priorityId: selectedPriorityId ? Number(selectedPriorityId) : null,
        warehouseLocationId: selectedWarehouseLocationId ? Number(selectedWarehouseLocationId) : null,
        costUsd: formData.costUsd.trim() === '' ? null : Number(formData.costUsd),
        ref: formData.ref.trim() || null,
        packageCount: formData.packageCount.trim() === '' ? null : Math.round(Number(formData.packageCount)),
        totalWeight: formData.totalWeight.trim() === '' ? null : Number(formData.totalWeight),
        notes: formData.notes.trim() || null,
        warehouseArrivalDate: formData.warehouseArrivalDate.trim() || null,
        warehouseDepartureDate: formData.warehouseDepartureDate.trim() || null,
        warehouseInvoiceNo: formData.warehouseInvoiceNo.trim() || null,
        warehouseCostUsd: formData.warehouseCostUsd.trim() === '' ? null : Number(formData.warehouseCostUsd),
        subAgentArrivalDate: formData.subAgentArrivalDate.trim() || null,
        subAgentDeliveryDate: formData.subAgentDeliveryDate.trim() || null,
        subAgentInvoiceNo: formData.subAgentInvoiceNo.trim() || null,
        subAgentCostUsd: formData.subAgentCostUsd.trim() === '' ? null : Number(formData.subAgentCostUsd),
        vesselAgentArrivalDate: formData.vesselAgentArrivalDate.trim() || null,
        vesselAgentDeliveryDate: formData.vesselAgentDeliveryDate.trim() || null,
        vesselAgentInvoiceNo: formData.vesselAgentInvoiceNo.trim() || null,
        vesselAgentCostUsd: formData.vesselAgentCostUsd.trim() === '' ? null : Number(formData.vesselAgentCostUsd),
      };
      if (isEdit && forwardingId) {
        await updateSpareForwarding(forwardingId, payload);
        navigate(returnPath ?? moduleConfig.module.route.detail(forwardingId));
      } else {
        const created = await createSpareForwarding(payload);
        const id = (created as { id?: number })?.id;
        if (id) navigate(returnPath ?? moduleConfig.module.route.detail(id));
        else goBack();
      }
    } finally {
      setIsSaving(false);
    }
  };

  if (isEdit && loadingExisting) {
    return (
      <div className={cn('flex min-h-[40vh] items-center justify-center rounded-xl border border-dashed px-4 py-16', colors.border)}>
        <p className={cn('text-sm font-medium', colors.textSecondary)}>Loading forwarding…</p>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-[67.2rem] space-y-6 pb-10">
      <div className="flex items-center gap-3">
        <Button type="button" variant="ghost" size="sm" className={cn('-ml-2 gap-1.5', colors.text)} onClick={goBack}>
          <ChevronLeftIcon className="h-4 w-4" />
          Back
        </Button>
      </div>

      <Card variant="outlined" className={cn('overflow-hidden border shadow-sm', colors.border)}>
        <div
          className={cn(
            'border-b px-4 py-4 sm:px-6 sm:py-5',
            colors.border,
            'bg-gradient-to-b from-slate-50/80 to-white dark:from-slate-900/40 dark:to-slate-900/20',
          )}
        >
          <h1 className={cn('text-xl font-semibold tracking-tight sm:text-2xl', colors.text)}>
            {isEdit ? moduleConfig.form.edit.title : moduleConfig.form.new.title}
          </h1>
          <p className={cn('mt-1 text-sm', colors.textSecondary)}>{moduleConfig.form.new.subtitle}</p>
        </div>

        <form onSubmit={handleSubmit} className={cn('space-y-6 px-4 py-5 sm:px-6', colors.text)}>
          <Accordion className="space-y-3">
            <AccordionItem
              title="Case & purchase order"
              description="Forwarding is always tied to a spare case. Optionally link a single purchase order."
              defaultOpen
              className={cn('overflow-hidden', colors.border, 'border')}
            >
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="min-w-0">
                  <Label>Spare case *</Label>
                  <Select
                    value={selectedCaseId}
                    onChange={(e) => {
                      setSelectedCaseId(e.target.value);
                      setSelectedPoId('');
                    }}
                    options={caseOptions}
                    placeholder="Select case…"
                    showClearButton={false}
                    disabled={isEdit}
                    className="mt-1.5 w-full"
                  />
                </div>
                <div className="min-w-0">
                  <Label>Purchase order (optional)</Label>
                  <Select
                    value={selectedPoId}
                    onChange={(e) => setSelectedPoId(e.target.value)}
                    options={poOptions}
                    placeholder="None"
                    showClearButton={false}
                    disabled={!selectedCaseId}
                    className="mt-1.5 w-full"
                  />
                </div>
              </div>
            </AccordionItem>

            <AccordionItem
              title="Logistics"
              description="Forwarder, discharge port, ETA, status, and shipment details."
              defaultOpen
              className={cn('overflow-hidden', colors.border, 'border')}
            >
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <Label>Forwarder (supplier)</Label>
                  <Select
                    value={selectedForwarderId}
                    onChange={(e) => {
                      setSelectedForwarderId(e.target.value);
                      setSelectedWarehouseLocationId('');
                    }}
                    options={supplierOptions}
                    placeholder="None"
                    showClearButton={false}
                    className="mt-1.5 w-full"
                  />
                </div>
                <div>
                  <Label>Port</Label>
                  <Select
                    value={selectedPortId}
                    onChange={(e) => setSelectedPortId(e.target.value)}
                    options={portOptions}
                    placeholder="None"
                    showClearButton={false}
                    className="mt-1.5 w-full"
                  />
                </div>
                <div>
                  <Label>ETA to port</Label>
                  <DateInput
                    value={formData.etaToPort}
                    onChange={(e) => setFormData((d) => ({ ...d, etaToPort: e.target.value }))}
                    className="mt-1.5"
                    showFormatHint={false}
                  />
                </div>
                <div>
                  <Label>Status</Label>
                  <Select
                    value={selectedStatusId}
                    onChange={(e) => setSelectedStatusId(e.target.value)}
                    options={statusOptions}
                    placeholder="Default (In transit)"
                    showClearButton={false}
                    className="mt-1.5 w-full"
                  />
                </div>
                <div>
                  <Label>Priority</Label>
                  <Select
                    value={selectedPriorityId}
                    onChange={(e) => setSelectedPriorityId(e.target.value)}
                    options={priorityOptions}
                    placeholder="None"
                    showClearButton={false}
                    className="mt-1.5 w-full"
                  />
                </div>
                <div>
                  <Label>Cost (USD)</Label>
                  <NumberInput
                    value={formData.costUsd}
                    onChange={(e) =>
                      setFormData((d) => ({ ...d, costUsd: e.target.value == null ? '' : String(e.target.value) }))
                    }
                    className="mt-1.5"
                    min={0}
                    step={0.01}
                  />
                </div>
                <div>
                  <Label>Warehouse location</Label>
                  <Select
                    value={selectedWarehouseLocationId}
                    onChange={(e) => setSelectedWarehouseLocationId(e.target.value)}
                    options={warehouseLocationOptions}
                    placeholder={selectedForwarderId ? 'None' : 'Select forwarder first'}
                    showClearButton={false}
                    disabled={!selectedForwarderId}
                    className="mt-1.5 w-full"
                  />
                </div>
                <div>
                  <Label>Reference</Label>
                  <TextInput
                    value={formData.ref}
                    onChange={(e) => setFormData((d) => ({ ...d, ref: e.target.value }))}
                    maxLength={30}
                    className="mt-1.5"
                    placeholder="HAWB / tracking / internal ref"
                  />
                </div>
                <div>
                  <Label>Package count</Label>
                  <NumberInput
                    value={formData.packageCount}
                    onChange={(e) =>
                      setFormData((d) => ({ ...d, packageCount: e.target.value == null ? '' : String(e.target.value) }))
                    }
                    className="mt-1.5"
                    min={0}
                  />
                </div>
                <div>
                  <Label>Total weight</Label>
                  <NumberInput
                    value={formData.totalWeight}
                    onChange={(e) =>
                      setFormData((d) => ({ ...d, totalWeight: e.target.value == null ? '' : String(e.target.value) }))
                    }
                    className="mt-1.5"
                    min={0}
                    step={0.01}
                  />
                </div>
                <div className="sm:col-span-2">
                  <Label>Notes</Label>
                  <Textarea
                    value={formData.notes}
                    onChange={(e) => setFormData((d) => ({ ...d, notes: e.target.value }))}
                    className={cn('mt-1.5 min-h-[100px]', colors.border)}
                    placeholder="Optional notes"
                  />
                </div>
              </div>
            </AccordionItem>

            <AccordionItem
              title="Warehouse (WH)"
              description="Arrival, departure, invoice, and cost in USD."
              defaultOpen={false}
              className={cn('overflow-hidden', colors.border, 'border')}
            >
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <Label>Arrival date</Label>
                  <DateInput
                    value={formData.warehouseArrivalDate}
                    onChange={(e) => setFormData((d) => ({ ...d, warehouseArrivalDate: e.target.value }))}
                    className="mt-1.5"
                    showFormatHint={false}
                  />
                </div>
                <div>
                  <Label>Departure date</Label>
                  <DateInput
                    value={formData.warehouseDepartureDate}
                    onChange={(e) => setFormData((d) => ({ ...d, warehouseDepartureDate: e.target.value }))}
                    className="mt-1.5"
                    showFormatHint={false}
                  />
                </div>
                <div>
                  <Label>Invoice no.</Label>
                  <TextInput
                    value={formData.warehouseInvoiceNo}
                    onChange={(e) => setFormData((d) => ({ ...d, warehouseInvoiceNo: e.target.value }))}
                    maxLength={80}
                    className="mt-1.5"
                  />
                </div>
                <div>
                  <Label>Cost (USD)</Label>
                  <NumberInput
                    value={formData.warehouseCostUsd}
                    onChange={(e) =>
                      setFormData((d) => ({
                        ...d,
                        warehouseCostUsd: e.target.value == null ? '' : String(e.target.value),
                      }))
                    }
                    className="mt-1.5"
                    min={0}
                    step={0.01}
                  />
                </div>
              </div>
            </AccordionItem>

            <AccordionItem
              title="Sub-agent (SA)"
              description="Arrival, delivery, invoice, and cost in USD."
              defaultOpen={false}
              className={cn('overflow-hidden', colors.border, 'border')}
            >
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <Label>Arrival date</Label>
                  <DateInput
                    value={formData.subAgentArrivalDate}
                    onChange={(e) => setFormData((d) => ({ ...d, subAgentArrivalDate: e.target.value }))}
                    className="mt-1.5"
                    showFormatHint={false}
                  />
                </div>
                <div>
                  <Label>Delivery date</Label>
                  <DateInput
                    value={formData.subAgentDeliveryDate}
                    onChange={(e) => setFormData((d) => ({ ...d, subAgentDeliveryDate: e.target.value }))}
                    className="mt-1.5"
                    showFormatHint={false}
                  />
                </div>
                <div>
                  <Label>Invoice no.</Label>
                  <TextInput
                    value={formData.subAgentInvoiceNo}
                    onChange={(e) => setFormData((d) => ({ ...d, subAgentInvoiceNo: e.target.value }))}
                    maxLength={80}
                    className="mt-1.5"
                  />
                </div>
                <div>
                  <Label>Cost (USD)</Label>
                  <NumberInput
                    value={formData.subAgentCostUsd}
                    onChange={(e) =>
                      setFormData((d) => ({
                        ...d,
                        subAgentCostUsd: e.target.value == null ? '' : String(e.target.value),
                      }))
                    }
                    className="mt-1.5"
                    min={0}
                    step={0.01}
                  />
                </div>
              </div>
            </AccordionItem>

            <AccordionItem
              title="Vessel agent (VA)"
              description="Arrival, delivery, invoice, and cost in USD."
              defaultOpen={false}
              className={cn('overflow-hidden', colors.border, 'border')}
            >
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <Label>Arrival date</Label>
                  <DateInput
                    value={formData.vesselAgentArrivalDate}
                    onChange={(e) => setFormData((d) => ({ ...d, vesselAgentArrivalDate: e.target.value }))}
                    className="mt-1.5"
                    showFormatHint={false}
                  />
                </div>
                <div>
                  <Label>Delivery date</Label>
                  <DateInput
                    value={formData.vesselAgentDeliveryDate}
                    onChange={(e) => setFormData((d) => ({ ...d, vesselAgentDeliveryDate: e.target.value }))}
                    className="mt-1.5"
                    showFormatHint={false}
                  />
                </div>
                <div>
                  <Label>Invoice no.</Label>
                  <TextInput
                    value={formData.vesselAgentInvoiceNo}
                    onChange={(e) => setFormData((d) => ({ ...d, vesselAgentInvoiceNo: e.target.value }))}
                    maxLength={80}
                    className="mt-1.5"
                  />
                </div>
                <div>
                  <Label>Cost (USD)</Label>
                  <NumberInput
                    value={formData.vesselAgentCostUsd}
                    onChange={(e) =>
                      setFormData((d) => ({
                        ...d,
                        vesselAgentCostUsd: e.target.value == null ? '' : String(e.target.value),
                      }))
                    }
                    className="mt-1.5"
                    min={0}
                    step={0.01}
                  />
                </div>
              </div>
            </AccordionItem>
          </Accordion>

          <div className="flex flex-wrap gap-2 border-t pt-4" style={{ borderColor: 'inherit' }}>
            <Button type="submit" disabled={isSaving || !selectedCaseId}>
              {isSaving ? moduleConfig.form.loadingText : isEdit ? moduleConfig.form.edit.submitText : moduleConfig.form.new.submitText}
            </Button>
            <Button type="button" variant="outline" onClick={goBack}>
              {moduleConfig.form.cancelText}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
};

export default Form;
